import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ScatterController,
  LineController,
  BarController,
  DoughnutController,
  PieController,
} from 'chart.js'
import type { ChartType } from 'chart.js'
import { Chart } from 'react-chartjs-2'
import type { ChartProps } from 'react-chartjs-2'
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot'
import { useRef } from 'react'
import { Stack, DefaultButton } from '@fluentui/react'

ChartJS.register(
  Title, Tooltip, Legend, CategoryScale, LinearScale,
  PointElement, LineElement, BarElement, ArcElement,
  BoxPlotController, BoxAndWiskers,
  ScatterController, LineController, BarController, DoughnutController, PieController,
)

type BoxPlotPoint = {
  min: number
  q1: number
  median: number
  q3: number
  max: number
}

type ChartPoint = number | BoxPlotPoint | null

const isBoxPlotPoint = (point: ChartPoint): point is BoxPlotPoint => typeof point === 'object' && point !== null

interface ChartData {
  type?: string
  title?: string
  xLabel?: string
  yLabel?: string
  labels: (string | number)[]
  datasets: Array<{
    label: string
    data: ChartPoint[]
    color?: string
    backgroundColor?: string
  }>
}

const COLORS = ['#2B3A67', '#496A99', '#8BACC4', '#5B8A72', '#D4775B', '#B8C47E', '#C8C1B1', '#3D5A80']

const isSingleValueDataset = (data: ChartPoint[]): boolean => {
  if (data.length === 0) return false
  return data.every((p) => isBoxPlotPoint(p) && p.min === p.q1 && p.q1 === p.median && p.median === p.q3 && p.q3 === p.max)
}

export function isChartJson(text: string): boolean {
  try {
    const parsed = JSON.parse(text.trim())
    return !!(parsed.labels && parsed.datasets)
  } catch {
    return false
  }
}

interface ChartFromJsonProps {
  chartData: ChartData
}

const ChartFromJson = ({ chartData }: ChartFromJsonProps) => {
  const labels = chartData.labels.map(String)
  const chartRef = useRef<ChartJS | null>(null)

  const exportPNG = () => {
    if (chartRef.current) {
      const a = document.createElement('a')
      a.href = chartRef.current.toBase64Image()
      a.download = `chart-${chartData.title || 'export'}.png`
      a.click()
    }
  }

  const exportCSV = () => {
    let csv = chartData.title ? `${chartData.title}\n\n` : ''
    csv += 'Label,' + chartData.datasets.map((ds) => `"${ds.label}"`).join(',') + '\n'
    chartData.labels.forEach((label, i) => {
      csv += `"${label}",` + chartData.datasets.map((ds) => ds.data[i] ?? '').join(',') + '\n'
    })
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `chart-${chartData.title || 'export'}.csv`
    a.click()
  }

  const chartType = (chartData.type || 'line').toLowerCase()
  const isPie = chartType === 'pie'
  const isBoxPlot = ['boxplot', 'box', 'box and whiskers', 'boxandwhiskers'].includes(chartType)

  const data = {
    labels,
    datasets: chartData.datasets.map((ds, i) => {
      const color = COLORS[i % COLORS.length]
      const isSingle = isBoxPlot && isSingleValueDataset(ds.data)

      if (isSingle) {
        return {
          type: 'scatter' as const,
          label: ds.label,
          data: ds.data.map((p, idx) => ({ x: labels[idx], y: isBoxPlotPoint(p) ? p.median : p })),
          borderColor: color,
          backgroundColor: color + '33',
          pointBackgroundColor: color,
          pointRadius: 6,
          showLine: false,
        }
      }
      if (isBoxPlot) {
        return {
          type: 'boxplot' as const,
          label: ds.label,
          data: ds.data,
          borderWidth: 2,
          backgroundColor: color + '30',
          borderColor: color,
          barPercentage: 0.5,
          categoryPercentage: 0.8,
        }
      }
      return {
        label: ds.label,
        data: ds.data,
        borderWidth: 2,
        tension: 0.3,
        borderColor: color,
        backgroundColor: isPie ? ds.data.map((_, j) => COLORS[j % COLORS.length]) : ds.backgroundColor || ds.color || color + '33',
        pointBackgroundColor: color,
      }
    }),
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: !!chartData.title, text: chartData.title, font: { size: 14, weight: 'bold' as const }, padding: 16 },
      legend: { position: 'top' as const },
    },
    scales: chartType !== 'pie' ? {
      x: {
        ...(isBoxPlot && { type: 'category' as const, offset: true }),
        title: { display: !!chartData.xLabel, text: chartData.xLabel },
      },
      y: {
        title: { display: !!chartData.yLabel, text: chartData.yLabel },
      },
    } : {},
  }

  let resolvedType = chartType as ChartType
  if (['boxplot', 'box', 'box and whiskers', 'boxandwhiskers'].includes(chartType)) resolvedType = 'boxplot'
  else if (!['bar', 'pie', 'scatter', 'line'].includes(chartType)) resolvedType = 'line'

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <div
        style={{
          width: '100%',
          height: isPie ? 450 : 400,
          padding: 16,
          backgroundColor: '#fff',
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Chart ref={chartRef} type={resolvedType} data={data as unknown as ChartProps['data']} options={options as ChartProps['options']} />
      </div>
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <DefaultButton text="Export PNG" iconProps={{ iconName: 'Download' }} onClick={exportPNG} />
        <DefaultButton text="Export CSV" iconProps={{ iconName: 'ExcelDocument' }} onClick={exportCSV} />
      </Stack>
    </Stack>
  )
}

export default ChartFromJson
